
import React, { useState, useMemo, useEffect } from 'react';
import { Student, TrialExamResult, Department, SavedTarget } from '../types';
import { api, API_URL, apiFetch } from '../services/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Legend, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
    PlusCircleIcon,
    TargetIcon,
    ExclamationIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChartBarIcon,
    TrendingUpIcon,
    AcademicCapIcon,
    SaveIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon,
    CheckIcon,
    XMarkIcon,
    LightBulbIcon,
    FireIcon
} from './icons';
import { AddExamModal } from './AddExamModal';

interface TargetAnalysisViewProps {
    student: Student;
    onUpdateStudent: (student: Student) => void;
}

export const TargetAnalysisView: React.FC<TargetAnalysisViewProps> = ({ student, onUpdateStudent }) => {
    const [isSetupMode, setIsSetupMode] = useState(!student.targetId);
    const [yokAtlasData, setYokAtlasData] = useState<any[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Save Target modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [targetName, setTargetName] = useState(student.targetName || '');
    const [isSavingTarget, setIsSavingTarget] = useState(false);

    useEffect(() => {
        // Fetch data using the centralized API_URL
        const fetchUrl = `${API_URL}/api/yokatlas/data?limit=5000`;

        apiFetch(fetchUrl)
            .then(res => res.json())
            .then(response => {
                // The API returns { total, data, limit, offset }
                setYokAtlasData(response.data || []);
                setIsLoadingData(false);
            })
            .catch(err => {
                console.error("Failed to load YÖK Atlas data from API:", err);
                setIsLoadingData(false);
            });
    }, []);

    const [setupForm, setSetupForm] = useState({
        type: '',
        dept: '',
        targetId: '',
        obp: student.obp || '' as number | ''
    });

    const [suggestedTargets, setSuggestedTargets] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Pre-fill form when editing existing target
    useEffect(() => {
        if (isSetupMode && yokAtlasData.length > 0 && student.targetId) {
            const existingTarget = yokAtlasData.find(d => d.id === student.targetId);
            if (existingTarget) {
                setSetupForm({
                    type: existingTarget.type || '',
                    dept: existingTarget.department || '',
                    targetId: student.targetId,
                    obp: student.obp || ''
                });
            }
        }
    }, [isSetupMode, yokAtlasData, student.targetId, student.obp]);

    // Derived Lists for Setup
    const availableDepts = useMemo(() => {
        if (!setupForm.type) return [];
        return Array.from(new Set(yokAtlasData.filter(d => d.type === setupForm.type).map(d => d.department))).sort();
    }, [setupForm.type, yokAtlasData]);

    const availableUnis = useMemo(() => {
        if (!setupForm.type || !setupForm.dept) return [];
        return yokAtlasData
            .filter(d => d.type === setupForm.type && d.department === setupForm.dept)
            .sort((a, b) => a.university.localeCompare(b.university));
    }, [setupForm.type, setupForm.dept, yokAtlasData]);

    // Auto-suggest targets based on OBP
    useEffect(() => {
        if (setupForm.obp > 0 && setupForm.type && yokAtlasData.length > 0) {
            const filtered = yokAtlasData.filter(d => d.type === setupForm.type);
            const obpScore = setupForm.obp * 5; // Convert 0-100 to 0-500 scale

            // Find programs where target OBP is close to student's OBP
            const suggestions = filtered
                .map(prog => {
                    const stat2024 = prog.stats?.find((s: any) => s.year === 2024);
                    const targetObp = stat2024?.obp || 0;
                    const diff = Math.abs(targetObp - obpScore);
                    return { ...prog, targetObp, diff };
                })
                .filter(p => p.diff < 50) // Within 50 points
                .sort((a, b) => a.diff - b.diff)
                .slice(0, 5);

            setSuggestedTargets(suggestions);
            setShowSuggestions(suggestions.length > 0);
        } else {
            setSuggestedTargets([]);
            setShowSuggestions(false);
        }
    }, [setupForm.obp, setupForm.type, yokAtlasData]);

    const [showAddExamModal, setShowAddExamModal] = useState(false);

    // OBP Edit State
    const [isEditingObp, setIsEditingObp] = useState(false);
    const [tempObp, setTempObp] = useState<number | ''>(student.obp || '');

    // Derived Data
    const targetData = useMemo(() => {
        if (isLoadingData) return null;
        const found = yokAtlasData.find(d => d.id === student.targetId);
        if (found) return found;
        return null; // Handle missing target gracefully
    }, [student.targetId, yokAtlasData, isLoadingData]);

    // OBP Logic
    const targetObp = targetData?.stats?.find((s: any) => s.year === 2025)?.obp || 0;
    const studentObp = student.obp || 0;
    const obpGap = targetObp - studentObp;
    const isObpRisk = obpGap > 0;
    const netAdjustmentFactor = isObpRisk ? 1 + (obpGap * 0.01) : 1;

    // Subject Configuration
    const getSubjectConfig = (type: string) => {
        const commonTYT = [
            { key: 'tytTurkce', label: 'TYT Türkçe', max: 40 },
            { key: 'tytMat', label: 'TYT Matematik', max: 40 },
            { key: 'tytFen', label: 'TYT Fen', max: 20 },
            { key: 'tytSos', label: 'TYT Sosyal', max: 20 },
        ];

        let fieldSubjects: any[] = [];
        if (type === 'SAY') {
            fieldSubjects = [
                { key: 'aytMat', label: 'AYT Matematik', max: 40 },
                { key: 'aytFizik', label: 'AYT Fizik', max: 14 },
                { key: 'aytKimya', label: 'AYT Kimya', max: 13 },
                { key: 'aytBiyoloji', label: 'AYT Biyoloji', max: 13 },
            ];
        } else if (type === 'EA') {
            fieldSubjects = [
                { key: 'aytMat', label: 'AYT Matematik', max: 40 },
                { key: 'aytEd', label: 'AYT Edebiyat', max: 24 },
                { key: 'aytTar1', label: 'AYT Tarih-1', max: 10 },
                { key: 'aytCog1', label: 'AYT Coğrafya-1', max: 6 },
            ];
        } else if (type === 'SOZ') {
            fieldSubjects = [
                { key: 'aytEd', label: 'AYT Edebiyat', max: 24 },
                { key: 'aytTar1', label: 'AYT Tarih-1', max: 10 },
                { key: 'aytCog1', label: 'AYT Coğrafya-1', max: 6 },
                { key: 'aytTar2', label: 'AYT Tarih-2', max: 11 },
                { key: 'aytCog2', label: 'AYT Coğrafya-2', max: 11 },
                { key: 'aytFel', label: 'AYT Felsefe', max: 12 },
                { key: 'aytDin', label: 'AYT Din', max: 6 },
            ];
        } else if (type === 'DIL') {
            fieldSubjects = [
                { key: 'ydt', label: 'YDT (Dil)', max: 80 },
            ];
        }

        return [...commonTYT, ...fieldSubjects];
    };

    const subjectConfig = useMemo(() => targetData ? getSubjectConfig(targetData.type) : [], [targetData]);

    const stats = useMemo(() => {
        if (!targetData) return [];
        const exams = student.trialExams || [];

        const getNet = (exam: TrialExamResult | undefined, key: string) => {
            if (!exam) return 0;
            const map: Record<string, string> = {
                'tytTurkce': 'Türkçe', 'tytMat': 'Matematik', 'tytFen': 'Fen Bilimleri', 'tytSos': 'Sosyal Bilimler',
                'aytMat': 'Matematik', 'aytFizik': 'Fizik', 'aytKimya': 'Kimya', 'aytBiyoloji': 'Biyoloji',
                'aytEd': 'Edebiyat', 'aytTar1': 'Tarih-1', 'aytCog1': 'Coğrafya-1',
                'aytTar2': 'Tarih-2', 'aytCog2': 'Coğrafya-2', 'aytFel': 'Felsefe', 'aytDin': 'Din Kültürü',
                'ydt': 'Yabancı Dil'
            };
            const subjectName = map[key] || key;
            return exam.scores?.[subjectName]?.net || 0;
        };

        return subjectConfig.map(sub => {
            const stat2023 = (targetData.stats.find((s: any) => s.year === 2023) as any)?.[sub.key] || 0;
            const stat2024 = (targetData.stats.find((s: any) => s.year === 2024) as any)?.[sub.key] || 0;
            const stat2025 = (targetData.stats.find((s: any) => s.year === 2025) as any)?.[sub.key] || 0;

            // ===== SCIENTIFIC TARGET CALCULATION =====

            // 1. Weighted Moving Average (2025: 50%, 2024: 30%, 2023: 20%)
            const weightedAvg = (stat2023 * 0.20) + (stat2024 * 0.30) + (stat2025 * 0.50);

            // 2. Trend Analysis (Linear projection)
            const trend = (stat2025 - stat2023) / 2;
            const projected2026 = stat2025 + trend;

            // 3. Standard Deviation for Safety Buffer
            const mean = (stat2023 + stat2024 + stat2025) / 3;
            const variance = ((stat2023 - mean) ** 2 + (stat2024 - mean) ** 2 + (stat2025 - mean) ** 2) / 3;
            const stdDev = Math.sqrt(variance);
            const safetyBuffer = stdDev * 1.0; // 1 σ for ~68% confidence

            // 4. OBP Adjustment
            let obpAdjustment = 0;
            if (targetObp > 0 && studentObp < targetObp) {
                const obpGapForSubject = targetObp - studentObp;
                obpAdjustment = obpGapForSubject * 0.02; // ~1 net per 50 OBP difference
            }

            // 5. Calculate 3-level targets (with fallback for missing data)
            // If all years have 0 data, use realistic defaults based on exam type
            const hasRealData = stat2023 > 0 || stat2024 > 0 || stat2025 > 0;
            let baseTarget = Math.max(projected2026, weightedAvg);

            // If no real data, use a realistic default (around 60-70% of max)
            if (!hasRealData) {
                baseTarget = sub.max * 0.65; // Realistic default
            }

            const minimumTarget = Math.min(Math.round(baseTarget * 10) / 10, sub.max * 0.95);
            const target2026 = Math.min(Math.round((baseTarget + safetyBuffer + obpAdjustment) * 10) / 10, sub.max * 0.95);
            const idealTarget = Math.min(Math.round((target2026 + 2) * 10) / 10, sub.max * 0.98);

            // 6. Trend direction
            let trendDirection: 'up' | 'down' | 'stable' = 'stable';
            if (trend > 0.5) trendDirection = 'up';
            else if (trend < -0.5) trendDirection = 'down';

            const average3Years = mean;

            let relevantExams = exams;
            if (sub.key.startsWith('tyt')) relevantExams = exams.filter(e => e.examType === 'TYT');
            else if (sub.key.startsWith('ayt')) relevantExams = exams.filter(e => e.examType === 'AYT');
            else if (sub.key === 'ydt') relevantExams = exams.filter(e => e.examType === 'YDT');

            const totalNet = relevantExams.reduce((acc, ex) => acc + getNet(ex, sub.key), 0);
            const average = relevantExams.length > 0 ? totalNet / relevantExams.length : 0;
            const latestRelevantExam = relevantExams[relevantExams.length - 1];
            const current = getNet(latestRelevantExam, sub.key);
            const gap2025 = current - stat2025;

            let status: 'CRITICAL_DROP' | 'STABLE' | 'IMPROVING' = 'STABLE';
            let statusLabel = 'Stabil';

            if (gap2025 < -2) {
                status = 'CRITICAL_DROP';
                statusLabel = 'Kritik Düşüş';
            } else if (gap2025 > 2) {
                status = 'IMPROVING';
                statusLabel = 'Gelişiyor';
            }

            return {
                key: sub.key,
                label: sub.label,
                fullMark: sub.max,
                stat2023,
                stat2024,
                stat2025,
                // Scientific targets
                minimumTarget,
                target2026,
                idealTarget,
                trendDirection,
                // Analysis data
                analysis: {
                    weightedAvg: Math.round(weightedAvg * 10) / 10,
                    projected: Math.round(projected2026 * 10) / 10,
                    stdDev: Math.round(stdDev * 10) / 10,
                    obpAdjustment: Math.round(obpAdjustment * 10) / 10
                },
                average3Years,
                average,
                current,
                progress: target2026 > 0 ? (current / target2026) * 100 : 0,
                gap2025,
                status,
                statusLabel
            };
        });
    }, [student.trialExams, subjectConfig, targetData, netAdjustmentFactor]);

    const [isSaving, setIsSaving] = useState(false);

    const handleSetupSave = async () => {
        if (!setupForm.targetId || !setupForm.obp) return;

        const selectedUni = yokAtlasData.find(u => u.id === setupForm.targetId);
        if (!selectedUni) return;

        const typeMap: Record<string, string> = {
            'SAY': 'Sayısal',
            'EA': 'Eşit Ağırlık',
            'SOZ': 'Sözel',
            'DIL': 'Yabancı Dil'
        };

        const updatedStudent: Student = {
            ...student,
            targetUni: selectedUni.university,
            targetDept: selectedUni.department,
            targetId: selectedUni.id,
            department: typeMap[setupForm.type] as any,
            obp: setupForm.obp === '' ? 0 : setupForm.obp
        };

        // Set mode BEFORE calling parent (prevents bounce-back from re-renders)
        setIsSetupMode(false);
        await onUpdateStudent(updatedStudent);
    };

    // Save target with custom name to savedTargets array
    const handleSaveTargetName = async () => {
        if (!targetName.trim()) {
            alert('Lütfen hedef için bir isim girin.');
            return;
        }

        // Use targetData (locally computed) as fallback for student props which may be stale
        const effectiveTargetId = student.targetId || targetData?.id || '';
        const effectiveTargetUni = student.targetUni || targetData?.university || '';
        const effectiveTargetDept = student.targetDept || targetData?.department || '';

        if (!effectiveTargetId || !effectiveTargetUni || !effectiveTargetDept) {
            alert('Önce bir hedef seçmeniz gerekiyor. Lütfen puan türü, bölüm ve üniversite seçerek analizi başlatın.');
            return;
        }

        setIsSavingTarget(true);

        try {
            const newSavedTarget: SavedTarget = {
                id: `target_${Date.now()}`,
                name: targetName.trim(),
                targetId: effectiveTargetId,
                targetUni: effectiveTargetUni,
                targetDept: effectiveTargetDept,
                type: targetData?.type || '',
                obp: student.obp || 0,
                savedAt: new Date().toISOString()
            };

            // Add to savedTargets, avoid duplicates by targetId
            const existingTargets = student.savedTargets || [];
            const filtered = existingTargets.filter(t => t.targetId !== effectiveTargetId);
            const updatedTargets = [...filtered, newSavedTarget];

            const updatedStudent: Student = {
                ...student,
                targetId: effectiveTargetId,
                targetUni: effectiveTargetUni,
                targetDept: effectiveTargetDept,
                targetName: targetName.trim(),
                savedTargets: updatedTargets
            };

            await onUpdateStudent(updatedStudent);
            setShowSaveModal(false);
        } catch (error) {
            console.error('Failed to save target:', error);
            alert('Hedef kaydedilemedi. Lütfen tekrar deneyin.');
        } finally {
            setIsSavingTarget(false);
        }
    };

    // Load a previously saved target
    const handleLoadSavedTarget = (saved: SavedTarget) => {
        const typeMap: Record<string, string> = {
            'SAY': 'Sayısal',
            'EA': 'Eşit Ağırlık',
            'SOZ': 'Sözel',
            'DIL': 'Yabancı Dil'
        };

        const updatedStudent: Student = {
            ...student,
            targetUni: saved.targetUni,
            targetDept: saved.targetDept,
            targetId: saved.targetId,
            targetName: saved.name,
            department: typeMap[saved.type] as any,
            obp: saved.obp
        };

        setIsSetupMode(false);
        onUpdateStudent(updatedStudent);
    };

    const handleSaveExam = (examData: TrialExamResult) => {
        const updatedStudent = {
            ...student,
            trialExams: [...student.trialExams, examData]
        };
        onUpdateStudent(updatedStudent);
        setShowAddExamModal(false);
    };

    const handleSaveObp = () => {
        const updatedStudent = {
            ...student,
            obp: tempObp === '' ? 0 : tempObp
        };
        onUpdateStudent(updatedStudent);
        setIsEditingObp(false);
    };

    if (isLoadingData) {
        return <div className="p-8 text-center text-gray-500">Veriler yükleniyor...</div>;
    }

    if (!isSetupMode && !targetData) {
        return (
            <div className="p-8 text-center text-gray-500">
                <p>Hedef verisi bulunamadı veya yüklenemedi.</p>
                <button onClick={() => setIsSetupMode(true)} className="mt-4 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg">
                    Tekrar Dene
                </button>
            </div>
        );
    }

    if (isSetupMode) {
        return (
            <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-xl border border-blue-100 dark:border-slate-700 max-w-2xl mx-auto my-10 animate-fade-in">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TargetIcon className="w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-white">Hedefini Belirle</h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">Sana özel analizler sunabilmemiz için hedefini ve okul puanını girmelisin.</p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Puan Türü</label>
                        <select
                            className="w-full p-4 rounded-xl border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={setupForm.type}
                            onChange={(e) => setSetupForm({ ...setupForm, type: e.target.value, dept: '', targetId: '' })}
                        >
                            <option value="">Seçiniz...</option>
                            <option value="SAY">SAYISAL (SAY)</option>
                            <option value="EA">EŞİT AĞIRLIK (EA)</option>
                            <option value="SOZ">SÖZEL (SÖZ)</option>
                            <option value="DIL">YABANCI DİL (DİL)</option>
                        </select>
                    </div>

                    <div className={!setupForm.type ? 'opacity-50 pointer-events-none' : ''}>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Bölüm</label>
                        <select
                            className="w-full p-4 rounded-xl border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={setupForm.dept}
                            onChange={(e) => setSetupForm({ ...setupForm, dept: e.target.value, targetId: '' })}
                            disabled={!setupForm.type}
                        >
                            <option value="">Seçiniz...</option>
                            {availableDepts.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>
                    </div>

                    <div className={!setupForm.dept ? 'opacity-50 pointer-events-none' : ''}>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Üniversite</label>
                        <select
                            className="w-full p-4 rounded-xl border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={setupForm.targetId}
                            onChange={(e) => setSetupForm({ ...setupForm, targetId: e.target.value })}
                            disabled={!setupForm.dept}
                        >
                            <option value="">Seçiniz...</option>
                            {availableUnis.map(d => (
                                <option key={d.id} value={d.id}>{d.university}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Diploma Notun (OBP)</label>
                        <input
                            type="number"
                            className="w-full p-4 rounded-xl border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            placeholder="Örn: 90"
                            value={setupForm.obp}
                            onChange={(e) => setSetupForm({ ...setupForm, obp: e.target.value === '' ? '' : Number(e.target.value) })}
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 ml-1">OBP, hedef netlerini hesaplarken kritik öneme sahiptir.</p>
                    </div>

                    {/* Auto Suggested Targets */}
                    {showSuggestions && suggestedTargets.length > 0 && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-700 dark:to-slate-800 p-6 rounded-2xl border border-blue-200 dark:border-slate-600">
                            <div className="flex items-center gap-2 mb-4">
                                <LightBulbIcon className="w-6 h-6 text-amber-500" />
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Sana Uygun Hedefler</h3>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">OBP'ne göre ulaşabileceğin hedefler:</p>
                            <div className="space-y-2">
                                {suggestedTargets.map((target, idx) => (
                                    <button
                                        key={target.id}
                                        onClick={() => {
                                            setSetupForm({
                                                ...setupForm,
                                                dept: target.department,
                                                targetId: target.id
                                            });
                                            setShowSuggestions(false);
                                        }}
                                        className={`w-full text-left p-4 rounded-xl border transition-all hover:scale-[1.02] ${idx === 0
                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30'
                                            : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-bold text-gray-800 dark:text-white">{target.university}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{target.department}</p>
                                            </div>
                                            {idx === 0 && (
                                                <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">En Uygun</span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleSetupSave}
                        disabled={!setupForm.targetId || !setupForm.obp}
                        className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-200 dark:shadow-none"
                    >
                        Analizi Başlat 🚀
                    </button>
                </div>

                {/* Previously Saved Targets */}
                {(student.savedTargets || []).length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-600">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                            <SaveIcon className="w-5 h-5 text-emerald-500" />
                            Kaydedilmiş Hedeflerim
                        </h3>
                        <div className="space-y-3">
                            {(student.savedTargets || []).map((saved) => (
                                <button
                                    key={saved.id}
                                    onClick={() => handleLoadSavedTarget(saved)}
                                    className="w-full text-left p-4 rounded-xl border bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-700 hover:shadow-md transition-all hover:scale-[1.01] group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-bold text-gray-800 dark:text-white text-lg">🎯 {saved.name}</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{saved.targetUni}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-500">{saved.targetDept} • {saved.type} • OBP: {saved.obp}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full opacity-0 group-hover:opacity-100 transition-opacity">Yükle</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (!targetData) return null; // Should be handled by guard above, but TS check

    return (
        <div className="space-y-8 animate-fade-in pb-24">
            <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 p-8 rounded-3xl shadow-sm border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full -mr-16 -mt-16 opacity-10 blur-3xl"></div>

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="px-3 py-1 bg-blue-500/20 text-blue-200 rounded-full text-xs font-bold uppercase tracking-wide border border-blue-400/30">{targetData.type}</span>
                            <span className="px-3 py-1 bg-white/10 text-gray-300 rounded-full text-xs font-bold">2025 Hedefi</span>
                            {student.targetName && (
                                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-bold border border-emerald-400/30">📌 {student.targetName}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <div>
                                <h1 className="text-3xl font-extrabold text-white leading-tight">{targetData.university}</h1>
                                <h2 className="text-xl font-bold text-gray-300 mt-1">{targetData.department}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Back to Setup / New Target */}
                        <button
                            onClick={() => {
                                setSetupForm({ type: '', dept: '', targetId: '', obp: student.obp || '' as number | '' });
                                setIsSetupMode(true);
                            }}
                            className="flex items-center gap-2 px-4 py-3 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-xl font-bold transition-all border border-white/10"
                            title="Yeni Hedef Oluştur"
                        >
                            <PlusCircleIcon className="w-5 h-5" />
                            Yeni Hedef
                        </button>
                        {/* Save Target Button */}
                        <button
                            onClick={() => { setTargetName(student.targetName || `${targetData.university} - ${targetData.department}`); setShowSaveModal(true); }}
                            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 active:scale-95"
                        >
                            <SaveIcon className="w-5 h-5" />
                            Hedefi Kaydet
                        </button>

                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                            <div className="text-center px-4 border-r border-white/10">
                                <p className="text-xs text-gray-400 font-bold uppercase">Hedef OBP</p>
                                <p className="text-xl font-extrabold text-white">{targetObp}</p>
                            </div>
                            <div className="text-center px-4">
                                <p className="text-xs text-gray-400 font-bold uppercase mb-2">Senin OBP</p>
                                {isEditingObp ? (
                                    <div className="flex items-center gap-2 bg-white shadow-lg p-2 rounded-xl border border-blue-200 absolute z-50 -ml-8 -mt-2 animate-scale-in">
                                        <input
                                            type="number"
                                            className="w-20 p-2 text-center border rounded-lg font-bold text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none text-lg"
                                            value={tempObp}
                                            onChange={(e) => setTempObp(e.target.value === '' ? '' : Number(e.target.value))}
                                            autoFocus
                                            onKeyDown={(e) => e.key === 'Enter' && handleSaveObp()}
                                        />
                                        <button onClick={handleSaveObp} className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors">
                                            <CheckIcon className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => setIsEditingObp(false)} className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className="flex items-center gap-2 group cursor-pointer bg-white hover:bg-gray-50 px-4 py-2 rounded-xl border border-transparent hover:border-gray-200 transition-all duration-200 relative"
                                        onClick={() => { setTempObp(studentObp || ''); setIsEditingObp(true); }}
                                        title="Düzenlemek için tıkla"
                                    >
                                        <div className="flex flex-col items-center">
                                            <p className={`text-2xl font-extrabold ${isObpRisk ? 'text-red-500' : 'text-green-600'}`}>
                                                {studentObp}
                                            </p>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                                            <PencilSquareIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                                        </div>
                                        {isObpRisk && (
                                            <div className="absolute -top-1 -right-1">
                                                <TooltipWrapper content={`Hedefin ${obpGap.toFixed(1)} puan altında. Net hedeflerin otomatik artırıldı.`}>
                                                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500 drop-shadow-sm" />
                                                </TooltipWrapper>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Net Analysis Chart */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 lg:col-span-2">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                                <ChartBarIcon className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Net Analizi & Hedefler</h3>
                        </div>
                    </div>

                    <div className="h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis
                                    dataKey="label"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#6B7280', fontSize: 11 }}
                                    angle={-45}
                                    textAnchor="end"
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#6B7280' }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                    cursor={{ fill: '#F3F4F6' }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Bar name="2023 Taban" dataKey="stat2023" fill="#E5E7EB" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="2024 Taban" dataKey="stat2024" fill="#9CA3AF" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="2025 Taban" dataKey="stat2025" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="📉 Minimum" dataKey="minimumTarget" fill="#FBBF24" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="🎯 Hedef" dataKey="target2026" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="⭐ İdeal" dataKey="idealTarget" fill="#EC4899" radius={[4, 4, 0, 0]} barSize={12} />
                                <Bar name="📊 Senin Netin" dataKey="current" fill="#10B981" radius={[4, 4, 0, 0]} barSize={12} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Premium Radar Chart Analysis */}
                <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-8 rounded-3xl shadow-2xl border border-blue-500/20 relative overflow-hidden">
                    {/* Animated background effects */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full -mr-48 -mt-48 opacity-15 blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500 rounded-full -ml-32 -mb-32 opacity-15 blur-3xl"></div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-blue-500/30">
                                <TargetIcon className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-extrabold text-white">Denge Analizi</h3>
                                <p className="text-blue-200/60 text-sm">Tüm derslerin kapsamlı performans karşılaştırması</p>
                            </div>
                        </div>

                        <div className="h-[420px] bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={stats}>
                                    <PolarGrid stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                                    <PolarAngleAxis
                                        dataKey="label"
                                        tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 600 }}
                                    />
                                    <PolarRadiusAxis
                                        angle={30}
                                        domain={[0, 'auto']}
                                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9 }}
                                        axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                    />
                                    {/* 7 Series with premium colors */}
                                    <Radar name="📊 2023 Taban" dataKey="stat2023" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.1} strokeWidth={1} strokeDasharray="5 5" />
                                    <Radar name="📈 2024 Taban" dataKey="stat2024" stroke="#64748B" fill="#64748B" fillOpacity={0.1} strokeWidth={1} strokeDasharray="3 3" />
                                    <Radar name="🎯 2025 Taban" dataKey="stat2025" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.15} strokeWidth={2} />
                                    <Radar name="📉 Minimum" dataKey="minimumTarget" stroke="#FBBF24" fill="#FBBF24" fillOpacity={0.1} strokeWidth={2} />
                                    <Radar name="🎯 Hedef 2026" dataKey="target2026" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} strokeWidth={3} />
                                    <Radar name="⭐ İdeal" dataKey="idealTarget" stroke="#EC4899" fill="#EC4899" fillOpacity={0.1} strokeWidth={2} strokeDasharray="8 4" />
                                    <Radar name="💪 Senin Netin" dataKey="current" stroke="#10B981" fill="#10B981" fillOpacity={0.35} strokeWidth={3} />
                                    <Legend
                                        wrapperStyle={{
                                            fontSize: '11px',
                                            color: 'white',
                                            paddingTop: '10px'
                                        }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(15, 23, 42, 0.95)',
                                            border: '1px solid rgba(139, 92, 246, 0.3)',
                                            borderRadius: '12px',
                                            color: 'white',
                                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                                        }}
                                        labelStyle={{ color: '#E879F9', fontWeight: 'bold' }}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Premium Legend Cards */}
                        <div className="grid grid-cols-7 gap-2 mt-6">
                            <div className="p-3 bg-slate-500/20 backdrop-blur rounded-xl border border-slate-400/20 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-slate-400 rounded-full mx-auto mb-2 ring-2 ring-slate-400/30"></div>
                                <span className="text-white/70 text-xs font-medium">2023</span>
                            </div>
                            <div className="p-3 bg-slate-600/20 backdrop-blur rounded-xl border border-slate-500/20 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-slate-500 rounded-full mx-auto mb-2 ring-2 ring-slate-500/30"></div>
                                <span className="text-white/70 text-xs font-medium">2024</span>
                            </div>
                            <div className="p-3 bg-blue-500/20 backdrop-blur rounded-xl border border-blue-400/30 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto mb-2 ring-2 ring-blue-400/50 animate-pulse"></div>
                                <span className="text-blue-200 text-xs font-medium">2025</span>
                            </div>
                            <div className="p-3 bg-amber-500/20 backdrop-blur rounded-xl border border-amber-400/30 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-amber-500 rounded-full mx-auto mb-2 ring-2 ring-amber-400/50"></div>
                                <span className="text-amber-200 text-xs font-medium">Min</span>
                            </div>
                            <div className="p-3 bg-purple-500/20 backdrop-blur rounded-xl border border-purple-400/30 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-purple-500 rounded-full mx-auto mb-2 ring-2 ring-purple-400/50 shadow-lg shadow-purple-500/50"></div>
                                <span className="text-purple-200 text-xs font-bold">Hedef</span>
                            </div>
                            <div className="p-3 bg-pink-500/20 backdrop-blur rounded-xl border border-pink-400/30 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-pink-500 rounded-full mx-auto mb-2 ring-2 ring-pink-400/50"></div>
                                <span className="text-pink-200 text-xs font-medium">İdeal</span>
                            </div>
                            <div className="p-3 bg-emerald-500/20 backdrop-blur rounded-xl border border-emerald-400/30 text-center group hover:scale-105 transition-all">
                                <div className="w-4 h-4 bg-emerald-500 rounded-full mx-auto mb-2 ring-2 ring-emerald-400/50 animate-pulse shadow-lg shadow-emerald-500/50"></div>
                                <span className="text-emerald-200 text-xs font-bold">Sen</span>
                            </div>
                        </div>

                        {/* Quick Stats Summary */}
                        <div className="mt-6 p-4 bg-white/5 backdrop-blur rounded-2xl border border-white/10">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                        <span className="text-2xl">📊</span>
                                    </div>
                                    <div>
                                        <p className="text-white/60 text-xs uppercase tracking-wide">Genel Durum</p>
                                        <p className="text-white font-bold text-lg">
                                            {stats.filter(s => s.current >= s.target2026).length} / {stats.length} derste hedefte
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {stats.filter(s => s.current < s.target2026 - 3).slice(0, 3).map(s => (
                                        <div key={s.key} className="px-3 py-1.5 bg-red-500/20 border border-red-400/30 rounded-lg">
                                            <span className="text-red-300 text-xs font-bold">{s.label.replace('TYT ', '').replace('AYT ', '')}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recommendations */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                            <LightBulbIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800">Gelişim Önerileri</h3>
                    </div>
                    <div className="space-y-4">
                        {stats.filter(s => s.gap2025 < -2).length === 0 ? (
                            <div className="p-4 bg-green-50 rounded-xl text-green-700 flex items-start gap-3">
                                <CheckCircleIcon className="w-6 h-6 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold">Harika Gidiyorsun!</p>
                                    <p className="text-sm mt-1">Tüm derslerde 2025 hedeflerine yakınsın veya üzerindesin.</p>
                                </div>
                            </div>
                        ) : (
                            stats.filter(s => s.gap2025 < -2).slice(0, 3).map(s => (
                                <div key={s.key} className="p-4 bg-red-50 rounded-xl text-red-700 flex items-start gap-3">
                                    <ExclamationCircleIcon className="w-6 h-6 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-bold">{s.label} Dersi Kritik!</p>
                                        <p className="text-sm mt-1">Hedefin {Math.abs(s.gap2025).toFixed(1)} net altındasın. Acilen konu eksiklerini kapatmalısın.</p>
                                    </div>
                                </div>
                            ))
                        )}
                        {stats.filter(s => s.gap2025 > 0).length > 0 && (
                            <div className="p-4 bg-blue-50 rounded-xl text-blue-700 flex items-start gap-3">
                                <TrendingUpIcon className="w-6 h-6 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold">Güçlü Yanların</p>
                                    <p className="text-sm mt-1">
                                        {stats.filter(s => s.gap2025 > 0).map(s => s.label).join(', ')} derslerinde hedefi geçtin!
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-center mt-8">
                <button
                    onClick={() => setShowAddExamModal(true)}
                    className="flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 transition-all font-bold text-lg hover:scale-105 active:scale-95"
                >
                    <PlusCircleIcon className="w-6 h-6" />
                    Yeni Deneme Sonucu Ekle
                </button>
            </div>

            {showAddExamModal && (
                <AddExamModal
                    onClose={() => setShowAddExamModal(false)}
                    onSave={handleSaveExam}
                />
            )}

            {/* Save Target Name Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 animate-scale-in">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <SaveIcon className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 dark:text-white">Hedefi Kaydet</h3>
                            <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Bu hedefe bir isim verin</p>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Hedef Ad&#305;</label>
                            <input
                                type="text"
                                className="w-full p-4 rounded-xl border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                placeholder="&#214;rn: T&#305;p Fak&#252;ltesi Hedefim"
                                value={targetName}
                                onChange={(e) => setTargetName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveTargetName()}
                                autoFocus
                            />
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 ml-1">
                                {targetData?.university} - {targetData?.department}
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowSaveModal(false)}
                                className="flex-1 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-600 transition-all"
                            >
                                &#304;ptal
                            </button>
                            <button
                                onClick={handleSaveTargetName}
                                disabled={!targetName.trim() || isSavingTarget}
                                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSavingTarget ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Kaydediliyor...
                                    </>
                                ) : (
                                    'Kaydet'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const TooltipWrapper: React.FC<{ content: string; children: React.ReactNode }> = ({ content, children }) => (
    <div className="group relative">
        {children}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            {content}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
        </div>
    </div>
);

// Icon component needed for recommendation
const ExclamationCircleIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
